/**
 * Date utility functions for handling local timezone dates
 * 
 * When parsing date strings like "2025-01-15", JavaScript's Date constructor
 * treats them as UTC midnight. This can cause the date to shift when displayed
 * in local time for users behind UTC.
 * 
 * These utilities ensure dates are parsed and formatted in local time.
 */

/**
 * Parse a date string as local time (not UTC)
 * Handles both date-only strings (YYYY-MM-DD) and full ISO strings
 * 
 * For date-only values (like due dates), we extract just the date portion
 * and treat it as local time to avoid timezone-related day shifts.
 * 
 * For datetime values (with time component), we convert to local time first
 * to get the correct local date.
 */
export const parseLocalDate = (dateString: string): Date => {
  if (!dateString) {
    return new Date();
  }
  
  // If it's just a date (YYYY-MM-DD), parse as local midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  
  // If it's an ISO string with T (e.g., "2025-12-09T03:00:00.000Z")
  if (dateString.includes('T')) {
    // Check if it has a timezone indicator (Z or +/-offset)
    const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    
    if (hasTimezone) {
      // This is a full datetime with timezone - convert to local time first
      // to get the correct local date (e.g., UTC Dec 9 3AM = PST Dec 8 7PM)
      const parsed = new Date(dateString);
      if (!isNaN(parsed.getTime())) {
        // Return a date object representing local midnight of that local day
        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      }
    }
    
    // No timezone - treat the date part as local (legacy behavior)
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  
  // Fallback: try to parse and extract date components
  const parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) {
    // If valid date, return it as-is (already in local time)
    return parsed;
  }
  
  return new Date();
};

/**
 * Format a date to YYYY-MM-DD in local timezone
 * Use this instead of toISOString().split('T')[0] to avoid UTC conversion
 */
export const toLocalDateString = (date: Date | string): string => {
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date as YYYY-MM-DD in local timezone
 */
export const getTodayLocalString = (): string => {
  return toLocalDateString(new Date());
};

/**
 * Parse a date-only string (YYYY-MM-DD) to form input value
 * Extracts just the date part if an ISO string is provided
 */
export const toDateInputValue = (dateString: string): string => {
  if (!dateString) return '';
  
  // If it's already YYYY-MM-DD, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // If it has a T (ISO format), extract just the date part
  // This preserves the date as-is from the server without timezone conversion
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  // Otherwise parse and format as local
  return toLocalDateString(dateString);
};

